import { cn } from 'lib/helpers/utility';

import Icon from 'components/shared/Icon/Icon';
import PracticeLogo from 'components/shared/PracticeLogo/PracticeLogo';

import myriadLogo from 'assets/Icons/logo.svg';

import { SAMPLE_EMAIL } from '../../lib/constants';

const DetailLabel = ({ icon, label, iconColor }) => (
  <div className="flex items-center gap-2">
    <Icon icon={icon} size={16} customColor={iconColor} />
    <span className="text-xs font-500 uppercase tracking-[0.06em] text-neutral-600">{label}</span>
  </div>
);

const formatAddress = (address) =>
  [address?.address_ln_1, address?.city, [address?.state, address?.zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

// `primaryHex` / `accentHex` are the resolved brand colors; the accent (two-color style only)
// is used for the small detail icons and the directions link.
const AppointmentEmailPreview = ({ practice, primaryHex, accentHex, isTiny = false }) => {
  const practiceName = practice?.dba_name || practice?.name || SAMPLE_EMAIL.fallbackPracticeName;
  const address = formatAddress(practice?.practiceAddress) || SAMPLE_EMAIL.fallbackAddress;
  const hasAccent = !!accentHex;
  const detailColor = hasAccent ? accentHex : undefined;
  const detailIconColor = hasAccent ? accentHex : primaryHex;

  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-[654px] flex-col gap-6 rounded-2xl border border-solid border-neutral-200 bg-white font-apple shadow-[0px_4px_24px_rgba(10,18,57,0.06)]',
        isTiny ? 'p-5' : 'p-8'
      )}>
      {practice?.header_photo ? (
        <PracticeLogo
          image={practice.header_photo}
          practiceId={practice.id}
          className="h-10 w-auto"
        />
      ) : (
        <img src={myriadLogo} alt="" className="h-10 w-auto self-start" />
      )}

      <span className="flex w-fit items-center gap-2 rounded-full border border-solid border-primary-200 bg-primary-50 px-4 py-2 text-xs font-600 uppercase tracking-[0.08em] text-primary-600">
        <Icon icon="check-mono" size={14} color="primary" shade={600} />
        Appointment scheduled
      </span>

      <h1 className="m-0 text-[28px] font-600 leading-9 text-primary-500">
        Your appointment is scheduled.
      </h1>

      <div className="flex flex-col gap-4 text-base leading-7 text-neutral-700">
        <p className="m-0">Hi {SAMPLE_EMAIL.patientFirstName},</p>
        <p className="m-0">
          We've scheduled your appointment with {practiceName}. Review the details below, then use
          the button to confirm your visit and complete your check-in forms - a few minutes now
          means less waiting when you arrive.
        </p>
      </div>

      <div
        className={cn(
          'flex flex-col gap-5 rounded-xl border border-solid border-primary-100 bg-primary-25',
          isTiny ? 'p-4' : 'p-6'
        )}>
        <div className="flex items-center gap-5">
          <div className="flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-lg bg-primary-500 text-white">
            <span className="text-xs font-600 uppercase tracking-[0.1em]">
              {SAMPLE_EMAIL.monthLabel}
            </span>
            <span className="text-[32px] font-600 leading-9">{SAMPLE_EMAIL.dayLabel}</span>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-lg font-600 text-primary-500">{SAMPLE_EMAIL.dateLabel}</span>
            <span className="flex items-center gap-2 text-base text-neutral-700">
              <Icon icon="clock-mono" size={16} color="primary" />
              <span className="font-600 text-primary-500">{SAMPLE_EMAIL.timeLabel}</span>
              {SAMPLE_EMAIL.timezoneLabel}
            </span>
          </div>
        </div>
        <div className="h-px w-full bg-primary-100" />
        <div className={cn('grid', isTiny ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-6')}>
          <div className="flex flex-col gap-2">
            <DetailLabel icon="user-mono" label="With" iconColor={detailIconColor} />
            <span className="pl-6 text-base font-500 text-neutral-900">
              {SAMPLE_EMAIL.practitionerName}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <DetailLabel icon="stethoscope-mono" label="Visit type" iconColor={detailIconColor} />
            <span className="pl-6 text-base font-500 text-neutral-900">
              {SAMPLE_EMAIL.visitType}
            </span>
          </div>
        </div>
        <div className="h-px w-full bg-primary-100" />
        <div className="flex flex-col gap-2">
          <DetailLabel icon="location-mono" label="Location" iconColor={detailIconColor} />
          <div className="flex flex-col gap-1 pl-6">
            <span className="text-base font-500 text-neutral-900">{practiceName}</span>
            <span className="text-sm text-neutral-700">{address}</span>
            <span
              className={cn('mt-1 text-sm font-600 underline', !hasAccent && 'text-primary-500')}
              style={{ color: detailColor }}>
              View directions
            </span>
          </div>
        </div>
      </div>

      <span className="flex w-fit items-center gap-2 rounded-full bg-primary-500 px-7 py-4 text-base font-600 text-white">
        Confirm appointment
        <Icon icon="arrow-right-mono" size={16} color="white" />
      </span>

      <p className="m-0 text-sm text-neutral-600">
        Need to make changes? Contact {practiceName} and we'll find a time that works for you.
      </p>
    </div>
  );
};

export default AppointmentEmailPreview;
